import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import {
  getUserSettings,
  updateUserSettings,
  resetUserSettings,
  getUserOllamaModels,
  getUserLLMStatus,
  getStravaStatus,
  type UserSettings as UserSettingsType,
  type EffectiveSettings,
  type OllamaModel,
  type StravaStatus,
} from '../api/user-settings';

type Tab = 'llm' | 'integrations';

export function UserSettings() {
  const [activeTab, setActiveTab] = useState<Tab>('llm');
  const [, setSettings] = useState<UserSettingsType | null>(null);
  const [effective, setEffective] = useState<EffectiveSettings | null>(null);
  const [ollamaModels, setOllamaModels] = useState<OllamaModel[]>([]);
  const [llmStatus, setLlmStatus] = useState<string>('checking');
  const [stravaStatus, setStravaStatus] = useState<StravaStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Form state - LLM
  const [provider, setProvider] = useState('');
  const [model, setModel] = useState('');
  const [ollamaUrl, setOllamaUrl] = useState('');
  const [anthropicKey, setAnthropicKey] = useState('');
  const [openaiKey, setOpenaiKey] = useState('');
  const [autoRouteImages, setAutoRouteImages] = useState(false);
  const [autoRouteDocuments, setAutoRouteDocuments] = useState(false);

  // Form state - Strava
  const [stravaClientId, setStravaClientId] = useState('');
  const [stravaClientSecret, setStravaClientSecret] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    if (provider === 'ollama' || (!provider && effective?.llm_provider === 'ollama')) {
      loadOllamaModels();
    }
  }, [provider, ollamaUrl, effective?.llm_provider]);

  useEffect(() => {
    if (activeTab === 'integrations') {
      loadStravaStatus();
    }
  }, [activeTab]);

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const [settingsRes, statusRes] = await Promise.all([
        getUserSettings(),
        getUserLLMStatus(),
      ]);

      if (settingsRes.success) {
        setSettings(settingsRes.settings);
        setEffective(settingsRes.effective);

        // Set form values from user settings (if any)
        const s = settingsRes.settings;
        setProvider(s.llm_provider || '');
        setModel(s.llm_model || '');
        setOllamaUrl(s.ollama_base_url || '');
        setAutoRouteImages(s.auto_route_images === 'true');
        setAutoRouteDocuments(s.auto_route_documents === 'true');
        setStravaClientId(s.strava_client_id || '');
        setStravaClientSecret(s.strava_client_secret ? '••••••••' : '');
      }

      if (statusRes.success) {
        setLlmStatus(statusRes.status);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      setMessage({ type: 'error', text: 'Errore nel caricamento delle impostazioni' });
    } finally {
      setIsLoading(false);
    }
  };

  const loadOllamaModels = async () => {
    try {
      const res = await getUserOllamaModels();
      if (res.success && res.models) {
        setOllamaModels(res.models);
        setLlmStatus('connected');
      }
    } catch {
      setOllamaModels([]);
      setLlmStatus('disconnected');
    }
  };

  const loadStravaStatus = async () => {
    try {
      const res = await getStravaStatus();
      if (res.success) {
        setStravaStatus(res.status);
      }
    } catch (error) {
      console.error('Error loading Strava status:', error);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMessage(null);

    try {
      const updates: Record<string, string> = {};

      // Only include values that have been set (not empty)
      if (provider) updates.llm_provider = provider;
      if (model) updates.llm_model = model;
      if (ollamaUrl) updates.ollama_base_url = ollamaUrl;

      // Auto-routing options
      if (autoRouteImages !== effective?.auto_route_images) {
        updates.auto_route_images = autoRouteImages ? 'true' : 'false';
      }
      if (autoRouteDocuments !== effective?.auto_route_documents) {
        updates.auto_route_documents = autoRouteDocuments ? 'true' : 'false';
      }

      // API keys (only if changed)
      if (anthropicKey && !anthropicKey.includes('...')) {
        updates.anthropic_api_key = anthropicKey;
      }
      if (openaiKey && !openaiKey.includes('...')) {
        updates.openai_api_key = openaiKey;
      }

      // Strava credentials
      if (stravaClientId) {
        updates.strava_client_id = stravaClientId;
      }
      if (stravaClientSecret && !stravaClientSecret.includes('•')) {
        updates.strava_client_secret = stravaClientSecret;
      }

      const res = await updateUserSettings(updates);
      if (res.success) {
        setMessage({ type: 'success', text: 'Impostazioni salvate con successo' });
        await loadSettings();
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Errore nel salvataggio',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('Vuoi ripristinare tutte le impostazioni ai valori predefiniti?')) return;

    setIsSaving(true);
    setMessage(null);

    try {
      const res = await resetUserSettings();
      if (res.success) {
        setMessage({ type: 'success', text: 'Impostazioni ripristinate ai valori predefiniti' });
        // Clear form state
        setProvider('');
        setModel('');
        setOllamaUrl('');
        setAnthropicKey('');
        setOpenaiKey('');
        setAutoRouteImages(false);
        setAutoRouteDocuments(false);
        setStravaClientId('');
        setStravaClientSecret('');
        await loadSettings();
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Errore nel ripristino',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const effectiveProvider = provider || effective?.llm_provider || 'ollama';
  const effectiveModel = model || effective?.llm_model || '';

  return (
    <Layout title="Impostazioni">
      <div className="max-w-4xl mx-auto">
        {/* Tabs */}
        <div className="flex gap-4 lg:gap-6 mb-8 border-b">
          {[
            { id: 'llm', label: 'LLM', icon: '🤖' },
            { id: 'integrations', label: 'Integrazioni', icon: '🔗' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as Tab)}
              className={`px-4 lg:px-6 py-4 lg:py-5 text-base lg:text-lg font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Info banner - only show on LLM tab */}
        {activeTab === 'llm' && (
          <div className="mb-6 lg:mb-8 p-4 lg:p-5 rounded-2xl bg-blue-50 text-blue-700 border border-blue-200">
            <p className="text-base lg:text-lg">
              <strong>Nota:</strong> Per le impostazioni LLM, se lasci un campo vuoto verranno
              utilizzate le impostazioni dell'amministratore come predefinite.
            </p>
          </div>
        )}

        {/* Message */}
        {message && (
          <div
            className={`mb-6 lg:mb-8 p-4 lg:p-5 rounded-2xl text-base lg:text-lg ${
              message.type === 'success'
                ? 'bg-green-50 text-green-700 border border-green-200'
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}
          >
            {message.text}
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-16">
            <p className="text-lg lg:text-xl text-gray-500">Caricamento...</p>
          </div>
        ) : (
          <>
            {/* LLM Tab */}
            {activeTab === 'llm' && (
              <div className="space-y-6 lg:space-y-8">
                {/* Status card */}
                <div className="bg-white rounded-2xl shadow-sm p-6 lg:p-8">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-lg lg:text-xl text-gray-900">Stato LLM</h3>
                      <p className="text-base lg:text-lg text-gray-500 mt-1">
                        Provider effettivo: {effectiveProvider}
                      </p>
                    </div>
                    <div
                      className={`px-4 py-2 rounded-xl text-base lg:text-lg font-medium ${
                        llmStatus === 'connected' || llmStatus === 'configured'
                          ? 'bg-green-100 text-green-700'
                          : llmStatus === 'checking'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {llmStatus === 'connected'
                        ? '● Connesso'
                        : llmStatus === 'configured'
                        ? '● Configurato'
                        : llmStatus === 'checking'
                        ? '○ Verifica...'
                        : '○ Non connesso'}
                    </div>
                  </div>
                </div>

                {/* Provider selection */}
                <div className="bg-white rounded-2xl shadow-sm p-6 lg:p-8">
                  <h3 className="font-semibold text-lg lg:text-xl text-gray-900 mb-3">Provider LLM</h3>
                  <p className="text-base lg:text-lg text-gray-500 mb-6">
                    Personalizza il modello di intelligenza artificiale da utilizzare
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-6">
                    <div>
                      <label className="block text-base lg:text-lg font-medium text-gray-700 mb-2">
                        Provider
                      </label>
                      <select
                        value={provider}
                        onChange={(e) => {
                          const newProvider = e.target.value;
                          setProvider(newProvider);
                          if (newProvider === 'anthropic') {
                            setModel('claude-sonnet-4-5-20250929');
                          } else if (newProvider === 'openai') {
                            setModel('gpt-4o');
                          } else if (newProvider === 'ollama' && ollamaModels.length > 0) {
                            setModel(ollamaModels[0].name);
                          } else {
                            setModel('');
                          }
                        }}
                        className="w-full px-4 py-3 text-base lg:text-lg border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Usa predefinito ({effective?.llm_provider || 'ollama'})</option>
                        <option value="ollama">Ollama (Locale)</option>
                        <option value="anthropic">Anthropic (Claude)</option>
                        <option value="openai">OpenAI (GPT)</option>
                      </select>
                      <p className="text-sm text-gray-500 mt-1">
                        Effettivo: {effectiveProvider}
                      </p>
                    </div>

                    <div>
                      <label className="block text-base lg:text-lg font-medium text-gray-700 mb-2">
                        Modello
                      </label>
                      {effectiveProvider === 'ollama' && ollamaModels.length > 0 ? (
                        <select
                          value={model}
                          onChange={(e) => setModel(e.target.value)}
                          className="w-full px-4 py-3 text-base lg:text-lg border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Usa predefinito ({effective?.llm_model || ''})</option>
                          {ollamaModels.map((m) => (
                            <option key={m.name} value={m.name}>
                              {m.name} ({m.size})
                            </option>
                          ))}
                        </select>
                      ) : effectiveProvider === 'anthropic' ? (
                        <select
                          value={model}
                          onChange={(e) => setModel(e.target.value)}
                          className="w-full px-4 py-3 text-base lg:text-lg border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Usa predefinito ({effective?.llm_model || ''})</option>
                          <option value="claude-sonnet-4-5-20250929">Claude Sonnet 4.5</option>
                          <option value="claude-sonnet-4-6">Claude Sonnet 4.6</option>
                          <option value="claude-opus-4-6">Claude Opus 4.6</option>
                          <option value="claude-haiku-4-5-20251001">Claude Haiku 4.5</option>
                        </select>
                      ) : effectiveProvider === 'openai' ? (
                        <select
                          value={model}
                          onChange={(e) => setModel(e.target.value)}
                          className="w-full px-4 py-3 text-base lg:text-lg border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Usa predefinito ({effective?.llm_model || ''})</option>
                          <option value="gpt-4o">GPT-4o</option>
                          <option value="gpt-4o-mini">GPT-4o Mini</option>
                          <option value="gpt-4-turbo">GPT-4 Turbo</option>
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={model}
                          onChange={(e) => setModel(e.target.value)}
                          placeholder={`Predefinito: ${effective?.llm_model || ''}`}
                          className="w-full px-4 py-3 text-base lg:text-lg border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      )}
                      <p className="text-sm text-gray-500 mt-1">
                        Effettivo: {effectiveModel}
                      </p>
                    </div>
                  </div>

                  {/* Ollama URL */}
                  {effectiveProvider === 'ollama' && (
                    <div className="mb-6">
                      <label className="block text-base lg:text-lg font-medium text-gray-700 mb-2">
                        URL Ollama
                      </label>
                      <input
                        type="text"
                        value={ollamaUrl}
                        onChange={(e) => setOllamaUrl(e.target.value)}
                        placeholder={`Predefinito: ${effective?.ollama_base_url || 'http://localhost:11434/v1'}`}
                        className="w-full px-4 py-3 text-base lg:text-lg border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        Effettivo: {ollamaUrl || effective?.ollama_base_url || 'http://localhost:11434/v1'}
                      </p>
                    </div>
                  )}

                  {/* API Keys */}
                  {effectiveProvider === 'anthropic' && (
                    <div className="mb-6">
                      <label className="block text-base lg:text-lg font-medium text-gray-700 mb-2">
                        Anthropic API Key
                      </label>
                      <input
                        type="password"
                        value={anthropicKey}
                        onChange={(e) => setAnthropicKey(e.target.value)}
                        placeholder={effective?.has_anthropic_key ? '••••••••••••••••' : 'sk-ant-...'}
                        className="w-full px-4 py-3 text-base lg:text-lg border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        {effective?.has_anthropic_key ? 'Una chiave API Anthropic è configurata (admin o tua)' : 'Nessuna chiave configurata'}
                      </p>
                    </div>
                  )}

                  {effectiveProvider === 'openai' && (
                    <div className="mb-6">
                      <label className="block text-base lg:text-lg font-medium text-gray-700 mb-2">
                        OpenAI API Key
                      </label>
                      <input
                        type="password"
                        value={openaiKey}
                        onChange={(e) => setOpenaiKey(e.target.value)}
                        placeholder={effective?.has_openai_key ? '••••••••••••••••' : 'sk-...'}
                        className="w-full px-4 py-3 text-base lg:text-lg border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        {effective?.has_openai_key ? 'Una chiave API OpenAI è configurata (admin o tua)' : 'Nessuna chiave configurata'}
                      </p>
                    </div>
                  )}
                </div>

                {/* Auto-routing options */}
                <div className="bg-white rounded-2xl shadow-sm p-6 lg:p-8">
                  <h3 className="font-semibold text-lg lg:text-xl text-gray-900 mb-3">
                    Routing Automatico
                  </h3>
                  <p className="text-base lg:text-lg text-gray-500 mb-6">
                    Usa automaticamente Anthropic per analizzare immagini o documenti
                  </p>

                  <div className="space-y-4">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={autoRouteImages}
                        onChange={(e) => setAutoRouteImages(e.target.checked)}
                        className="w-5 h-5 rounded text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-base lg:text-lg text-gray-700">
                        Usa Anthropic per analisi immagini
                      </span>
                    </label>

                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={autoRouteDocuments}
                        onChange={(e) => setAutoRouteDocuments(e.target.checked)}
                        className="w-5 h-5 rounded text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-base lg:text-lg text-gray-700">
                        Usa Anthropic per analisi documenti PDF
                      </span>
                    </label>
                  </div>
                </div>

                {/* Save button */}
                <div className="flex gap-4">
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="flex-1 py-4 px-6 text-base lg:text-lg font-medium bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    {isSaving ? 'Salvataggio...' : 'Salva Impostazioni'}
                  </button>
                  <button
                    onClick={handleReset}
                    disabled={isSaving}
                    className="py-4 px-6 text-base lg:text-lg font-medium bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 disabled:opacity-50 transition-colors"
                  >
                    Ripristina
                  </button>
                </div>
              </div>
            )}

            {/* Integrations Tab */}
            {activeTab === 'integrations' && (
              <div className="space-y-6 lg:space-y-8">
                {/* Strava card */}
                <div className="bg-white rounded-2xl shadow-sm p-6 lg:p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 lg:w-14 lg:h-14 bg-orange-100 rounded-xl flex items-center justify-center">
                      <svg className="w-7 h-7 lg:w-8 lg:h-8 text-orange-600" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg lg:text-xl text-gray-900">Strava</h3>
                      <p className="text-base lg:text-lg text-gray-500">
                        Sincronizza i tuoi allenamenti da Strava
                      </p>
                    </div>
                    {stravaStatus && (
                      <div
                        className={`px-4 py-2 rounded-xl text-base lg:text-lg font-medium ${
                          stravaStatus.isConnected && !stravaStatus.isExpired
                            ? 'bg-green-100 text-green-700'
                            : stravaStatus.hasCredentials
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {stravaStatus.isConnected && !stravaStatus.isExpired
                          ? '● Connesso'
                          : stravaStatus.hasCredentials
                          ? '○ Credenziali configurate'
                          : '○ Non configurato'}
                      </div>
                    )}
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                    <h4 className="font-medium text-blue-900 mb-2">Come ottenere le credenziali Strava</h4>
                    <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                      <li>
                        Vai su{' '}
                        <a
                          href="https://www.strava.com/settings/api"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline hover:text-blue-600"
                        >
                          strava.com/settings/api
                        </a>
                      </li>
                      <li>Crea una nuova applicazione</li>
                      <li>Copia Client ID e Client Secret qui sotto</li>
                      <li>Imposta il Callback Domain: <code className="bg-blue-100 px-1 rounded">localhost</code></li>
                    </ol>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-base lg:text-lg font-medium text-gray-700 mb-2">
                        Client ID
                      </label>
                      <input
                        type="text"
                        value={stravaClientId}
                        onChange={(e) => setStravaClientId(e.target.value)}
                        placeholder={effective?.has_strava_credentials ? 'Configurato' : 'Inserisci Client ID'}
                        className="w-full px-4 py-3 text-base lg:text-lg border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-base lg:text-lg font-medium text-gray-700 mb-2">
                        Client Secret
                      </label>
                      <input
                        type="password"
                        value={stravaClientSecret}
                        onChange={(e) => setStravaClientSecret(e.target.value)}
                        placeholder={effective?.has_strava_credentials ? '••••••••' : 'Inserisci Client Secret'}
                        className="w-full px-4 py-3 text-base lg:text-lg border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  {!effective?.has_strava_credentials && (
                    <p className="text-sm text-amber-600 mt-3">
                      Devi configurare le tue credenziali Strava per poter sincronizzare gli allenamenti.
                    </p>
                  )}
                </div>

                {/* Save button */}
                <div className="flex gap-4">
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="flex-1 py-4 px-6 text-base lg:text-lg font-medium bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    {isSaving ? 'Salvataggio...' : 'Salva Impostazioni'}
                  </button>
                  <button
                    onClick={handleReset}
                    disabled={isSaving}
                    className="py-4 px-6 text-base lg:text-lg font-medium bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 disabled:opacity-50 transition-colors"
                  >
                    Ripristina
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
